import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { Loader2, ArrowLeft, Save } from 'lucide-react'
import { marked } from 'marked'
import { apiGet, slugify } from '../lib/api'
import { RichEditor } from '../components/RichEditor'

/** Detecta si el string ya es HTML (tiene tags) o es Markdown legacy. */
function isHtml(s: string): boolean {
  return /<[a-z][\s\S]*>/i.test(s)
}

/** Convierte legacy markdown → HTML para que CKEditor lo edite. */
function ensureHtml(content: string): string {
  if (!content?.trim()) return ''
  if (isHtml(content)) return content
  try {
    return marked.parse(content, { async: false }) as string
  } catch {
    return content
  }
}

interface PaginaForm {
  slug: string
  title: string
  content: string
  excerpt: string
  meta_title: string
  meta_description: string
  meta_keywords: string
  og_image: string
  is_published: boolean
  show_in_footer: boolean
  footer_section: string
  sort_order: number
}

const empty: PaginaForm = {
  slug: '',
  title: '',
  content: '',
  excerpt: '',
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  og_image: '',
  is_published: true,
  show_in_footer: false,
  footer_section: '',
  sort_order: 0,
}

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

interface Props {
  mode: 'create' | 'edit'
}

export function PaginaForm({ mode }: Props) {
  const params = mode === 'edit' ? useParams({ strict: false }) : { id: '' }
  const id = (params as any).id as string
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [form, setForm] = useState<PaginaForm>(empty)
  const [err, setErr] = useState<string | null>(null)
  const [autoSlug, setAutoSlug] = useState(mode === 'create')

  const { data, isLoading } = useQuery<any>({
    queryKey: ['pagina', id],
    queryFn: () => apiGet(`/api/admin/paginas/${id}`),
    enabled: mode === 'edit',
  })

  useEffect(() => {
    if (mode === 'edit' && data) {
      setForm({
        slug: data.slug ?? '',
        title: data.title ?? '',
        content: ensureHtml(data.content ?? ''),
        excerpt: data.excerpt ?? '',
        meta_title: data.metaTitle ?? '',
        meta_description: data.metaDescription ?? '',
        meta_keywords: data.metaKeywords ?? '',
        og_image: data.ogImage ?? '',
        is_published: !!data.isPublished,
        show_in_footer: !!data.showInFooter,
        footer_section: data.footerSection ?? '',
        sort_order: data.sortOrder ?? 0,
      })
      setAutoSlug(false)
    }
  }, [data, mode])

  const save = useMutation({
    mutationFn: async (payload: PaginaForm) => {
      const url = mode === 'edit' ? `/api/admin/paginas/${id}` : `/api/admin/paginas`
      const method = mode === 'edit' ? 'PUT' : 'POST'
      const body = {
        slug: payload.slug,
        title: payload.title,
        content: payload.content,
        excerpt: payload.excerpt || null,
        meta_title: payload.meta_title || null,
        meta_description: payload.meta_description || null,
        meta_keywords: payload.meta_keywords || null,
        og_image: payload.og_image || null,
        is_published: payload.is_published,
        show_in_footer: payload.show_in_footer,
        footer_section: payload.footer_section,
        sort_order: Number(payload.sort_order) || 0,
      }
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      return j
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['paginas'] })
      qc.invalidateQueries({ queryKey: ['pagina', id] })
      navigate({ to: '/paginas' })
    },
    onError: (e: Error) => setErr(e.message),
  })

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr(null)
    if (!SLUG_RE.test(form.slug)) {
      setErr('El slug solo puede contener minúsculas, números y guiones (ej. "como-comprar").')
      return
    }
    save.mutate(form)
  }

  function set<K extends keyof PaginaForm>(k: K, v: PaginaForm[K]) {
    setForm((f) => ({ ...f, [k]: v }))
  }

  function onTitleChange(v: string) {
    set('title', v)
    if (autoSlug) set('slug', slugify(v))
  }

  const title = mode === 'create' ? 'Nueva página' : 'Editar página'
  const submitting = save.isPending

  return (
    <>
      <Topbar
        title={title}
        subtitle={form.slug ? `/${form.slug}` : undefined}
        actions={
          <Link
            to="/paginas"
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
          >
            <ArrowLeft className="size-3.5" /> Cancelar
          </Link>
        }
      />

      <div className="p-6">
        {mode === 'edit' && isLoading && (
          <div className="grid place-items-center py-20 text-ink-500">
            <Loader2 className="size-8 animate-spin" />
          </div>
        )}

        {(mode === 'create' || !isLoading) && (
          <form onSubmit={onSubmit} className="mx-auto max-w-6xl space-y-6">
            {err && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {err}
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Columna principal */}
              <div className="space-y-6 lg:col-span-2">
                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <div className="grid grid-cols-1 gap-4">
                    <Field label="Título *">
                      <input
                        required
                        value={form.title}
                        onChange={(e) => onTitleChange(e.target.value)}
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-base font-semibold outline-none focus:border-brand-500"
                      />
                    </Field>

                    <Field
                      label="Slug *"
                      hint='Solo minúsculas, números y guiones. Ej. "como-comprar".'
                    >
                      <div className="flex gap-2">
                        <span className="grid place-items-center rounded-lg border border-ink-200 bg-ink-50 px-3 text-xs text-ink-500">
                          /
                        </span>
                        <input
                          required
                          value={form.slug}
                          onChange={(e) => {
                            setAutoSlug(false)
                            set('slug', e.target.value.toLowerCase())
                          }}
                          pattern={SLUG_RE.source}
                          className="flex-1 rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setAutoSlug(true)
                            set('slug', slugify(form.title))
                          }}
                          className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-xs font-medium text-ink-700 hover:bg-ink-50"
                        >
                          Auto
                        </button>
                      </div>
                    </Field>

                    <Field label="Resumen (excerpt)" hint="Texto corto para listados internos.">
                      <textarea
                        rows={2}
                        maxLength={500}
                        value={form.excerpt}
                        onChange={(e) => set('excerpt', e.target.value)}
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </Field>

                    <Field label="Contenido *" hint="Editor visual — se guarda como HTML y se renderiza en el sitio público.">
                      <RichEditor
                        value={form.content}
                        onChange={(v) => set('content', v)}
                        placeholder="Escribe el contenido de la página…"
                        minHeight={500}
                      />
                    </Field>
                  </div>
                </div>

                {/* SEO */}
                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <h2 className="mb-4 font-semibold text-ink-900">SEO</h2>
                  <div className="grid grid-cols-1 gap-4">
                    <Field label="Meta title">
                      <input
                        value={form.meta_title}
                        maxLength={180}
                        onChange={(e) => set('meta_title', e.target.value)}
                        placeholder={form.title || 'Título para buscadores'}
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </Field>
                    <Field label="Meta description">
                      <textarea
                        rows={2}
                        value={form.meta_description}
                        onChange={(e) => set('meta_description', e.target.value)}
                        placeholder="Resumen para buscadores (140-160 caracteres)"
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </Field>
                    <Field label="Meta keywords">
                      <input
                        value={form.meta_keywords}
                        onChange={(e) => set('meta_keywords', e.target.value)}
                        placeholder="separadas, por, comas"
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </Field>
                    <Field label="URL imagen Open Graph">
                      <input
                        value={form.og_image}
                        onChange={(e) => set('og_image', e.target.value)}
                        placeholder="https://…"
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
                      />
                      {form.og_image && (
                        <img
                          src={form.og_image}
                          alt=""
                          className="mt-2 h-32 w-full rounded-lg object-cover"
                          onError={(e) => {
                            ;(e.target as HTMLImageElement).style.display = 'none'
                          }}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6 lg:col-span-1">
                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <h2 className="mb-4 font-semibold text-ink-900">Publicación</h2>
                  <div className="space-y-4">
                    <label className="inline-flex items-center gap-2 text-sm text-ink-700">
                      <input
                        type="checkbox"
                        checked={form.is_published}
                        onChange={(e) => set('is_published', e.target.checked)}
                        className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                      />
                      Publicada (visible en el sitio)
                    </label>
                  </div>
                </div>

                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <h2 className="mb-4 font-semibold text-ink-900">Footer del sitio</h2>
                  <div className="space-y-4">
                    <label className="inline-flex items-center gap-2 text-sm text-ink-700">
                      <input
                        type="checkbox"
                        checked={form.show_in_footer}
                        onChange={(e) => set('show_in_footer', e.target.checked)}
                        className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                      />
                      Mostrar en footer
                    </label>
                    <Field label="Sección del footer">
                      <select
                        value={form.footer_section}
                        onChange={(e) => set('footer_section', e.target.value)}
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      >
                        <option value="">— Sin sección —</option>
                        <option value="catalogo">Catálogo</option>
                        <option value="soporte">Soporte</option>
                        <option value="maz">Maz</option>
                      </select>
                    </Field>
                    <Field label="Orden" hint="Menor número aparece primero.">
                      <input
                        type="number"
                        value={form.sort_order}
                        onChange={(e) => set('sort_order', Number(e.target.value))}
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </Field>
                  </div>
                </div>

                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
                  <div className="mb-1 font-semibold">URL pública</div>
                  <div className="font-mono">
                    /{form.slug || '…'}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Link
                to="/paginas"
                className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-100"
              >
                Cancelar
              </Link>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {submitting ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Save className="size-3.5" />
                )}
                {mode === 'create' ? 'Crear página' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-ink-700">{label}</label>
      {children}
      {hint && <div className="mt-1 text-[11px] text-ink-500">{hint}</div>}
    </div>
  )
}

/**
 * Render markdown minimal solo para preview en el admin.
 * En el frontend público usamos react-markdown.
 */
function renderMarkdownPreview(md: string): string {
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

  let html = escape(md)

  // Headings
  html = html.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>')
  html = html.replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>')
  html = html.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>')
  html = html.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>')
  html = html.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>')
  html = html.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>')

  // Bold + italic
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/(^|\W)_([^_]+)_(?=\W|$)/g, '$1<em>$2</em>')

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>')

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')

  // Blockquotes
  html = html.replace(/^&gt;\s+(.+)$/gm, '<blockquote>$1</blockquote>')

  // Unordered lists
  html = html.replace(/(?:^- .+(?:\n|$))+?/gm, (block) => {
    const items = block
      .trim()
      .split('\n')
      .map((l) => `<li>${l.replace(/^- /, '')}</li>`)
      .join('')
    return `<ul>${items}</ul>`
  })

  // Ordered lists
  html = html.replace(/(?:^\d+\. .+(?:\n|$))+?/gm, (block) => {
    const items = block
      .trim()
      .split('\n')
      .map((l) => `<li>${l.replace(/^\d+\.\s+/, '')}</li>`)
      .join('')
    return `<ol>${items}</ol>`
  })

  // Paragraphs (líneas sueltas)
  html = html
    .split(/\n\n+/)
    .map((block) => {
      if (/^<(h\d|ul|ol|blockquote|pre|table)/.test(block.trim())) return block
      if (!block.trim()) return ''
      return `<p>${block.replace(/\n/g, '<br/>')}</p>`
    })
    .join('\n')

  return html
}
